import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatSnackBar,
} from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { ErgonomicsEvaluationReportDialogComponent } from "app/assessment/evaluation/components/ergonomics-evaluation-report-dialog/ergonomics-evaluation-report-dialog.component";
import { EvaluationService } from "app/assessment/evaluation/services/evaluation.service";
import { EntityType, EntityTypeDynamic } from "app/assessment/models";
import { SurveyReportDialogComponent } from "app/assessment/survey/components/survey-report-dialog/survey-report-dialog.component";
import { AuthService } from "app/core/authentication/services";
import { BrowserService } from "app/core/browser/services/browser.service";
import { UserInterface } from "app/core/user/models";
import { UserService } from "app/core/user/services";
import utilityCommon from "app/core/utility/utility-common";
import { RelatedEntity, TaskInterface } from "app/task/models";
import { TaskStatus } from "app/task/models/task-status.enum";
import { TaskService } from "app/task/services";
import { mapStatus } from "app/task/utilities/task-card-status-mapper.utility";
import { Observable, Subject } from "rxjs";
import { map, startWith, takeUntil } from "rxjs/operators";

@Component({
  selector: "cz-task-card-dialog",
  templateUrl: "./task-card-dialog.component.html",
  styleUrls: ["./task-card-dialog.component.scss"],
})
export class TaskCardDialogComponent implements OnInit, OnDestroy {
  public allAssignedToOptions: UserInterface[] = [];
  public assignedToFilteredOptions: Observable<UserInterface[]>;
  public showComments: boolean = false;
  public indicateComments: boolean = false;
  public task: TaskInterface;
  public taskFormGroup: FormGroup;
  public canReassign: boolean = false;
  public canEdit: boolean = false;
  public hasPdfButton: boolean = false;
  public hasTaskButton: boolean = false;
  public isLinked: boolean = false;
  public isReadonly: boolean = false;
  public relatedEntity: RelatedEntity;
  public mapStatus: Function = mapStatus;
  public visibleToForUserControl: FormControl;
  public isLinkedToEval: boolean = false;
  public isAdmin: boolean = false;
  public browser: string = "unknown";
  public loggedInUser: any;
  private readonly _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly _browserService: BrowserService,
    public dialog: MatDialog,
    private readonly _dialogRef: MatDialogRef<TaskCardDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _formBuilder: FormBuilder,
    private readonly _taskService: TaskService,
    private readonly _userService: UserService,
    private readonly _evaluationService: EvaluationService,
    private _translateService: TranslateService,
    private readonly _snackBar: MatSnackBar,
    private readonly _authService: AuthService
  ) {
    // Get Browser name
    this.browser = utilityCommon.getBrowserName();
    this._userService
      .findAllUsersForAssignedToList()
      .pipe(takeUntil(this._destroy$))
      .subscribe((users: UserInterface[]) => {
        this.allAssignedToOptions = users;
      });
  }

  public ngOnInit(): void {
    // Verify the provided data contains a task.
    if (!this.data.task) {
      // Static text to Language Variable updated
      throw new Error(
        this._translateService.instant("$message_no _task_provided.")
      );
    }
    this._authService
      .getAuthenticatedUser()
      .pipe(takeUntil(this._destroy$))
      .subscribe((authenticatedUser: UserInterface) => {
        if (authenticatedUser === undefined) {
          return;
        }
        const loggedInUser = authenticatedUser;
        this._userService
          .findOne(loggedInUser.id)
          .pipe(takeUntil(this._destroy$))
          .subscribe((authenticatedUser: UserInterface) => {
            this.loggedInUser = authenticatedUser;
          });
      });

    this.task = this.data.task;
    this.isReadonly = this.task.isComplete;
    this.showComments = this.data.showComments;
    this.canReassign =
      this.data.isAdmin &&
      this.task.relatedEntity.entityType !== EntityType.SURVEY;
    this.canEdit = this.data.isAdmin;
    this.relatedEntity = this.task.relatedEntity;
    this.isLinked =
      this.relatedEntity && this.relatedEntity.entityType !== EntityType.NONE
        ? true
        : false;
    // The task button is visible for uncompleted unlinked tasks, uncompleted surveys, or any status of evaluation.
    this.hasTaskButton = !this.task.isComplete && this.data.isAssignedTo;
    // The PDF button is visible for completed linked tasks.
    this.hasPdfButton =
      this.isLinked && this.task.status === TaskStatus.COMPLETE;
    this.isLinkedToEval =
      this.task.relatedEntity &&
      this.task.relatedEntity.entityType === EntityType.EVALUATION;
    this.isAdmin = this.data.isAdmin;

    this.visibleToForUserControl = new FormControl({ value: undefined });

    // TODO - need to add logic for handling Comments
    this.taskFormGroup = this._formBuilder.group({
      assignedToCtrl: [
        { value: "", disabled: !this.canReassign || this.isReadonly },
        Validators.required,
      ],
      completedByCtrl: [{ value: "", disabled: true }],
      completedDateCtrl: [{ value: "", disabled: this.isReadonly }],
      dueDateCtrl: [
        { value: "", disabled: !this.canEdit || this.isReadonly },
        Validators.required,
      ],
      hoursCtrl: [
        { value: "", disabled: !this.canEdit || this.isReadonly },
        Validators.required,
      ],
      notesCtrl: [{ value: "", disabled: !this.canEdit || this.isReadonly }],
      summaryCtrl: [{ value: "", disabled: !this.canEdit || this.isReadonly }],
    });

    this.assignedToFilteredOptions = this.taskFormGroup
      .get("assignedToCtrl")
      .valueChanges.pipe(
        startWith<string | UserInterface>(""),
        map((value: any) =>
          typeof value === "string" ? value : value.firstName
        ),
        map((name: string) =>
          name
            ? this.filterAssignToUserOptions(name)
            : this.allAssignedToOptions.slice()
        )
      );

    if (this.task.comments && this.task.comments.length > 0) {
      this.indicateComments = true;
    }

    this.taskFormGroup.patchValue({
      assignedToCtrl: this.task.assignedTo ? this.task.assignedTo : "",
      completedByCtrl: this.task.isComplete
        ? this.autocompleteDisplayUserOption(this.task.completedBy)
        : "",
      completedDateCtrl: this.task.completedDate ? this.task.completedDate : "",
      dueDateCtrl: this.task.dueDate ? this.task.dueDate : "",
      hoursCtrl: this.task.hours ? this.task.hours : 0,
      notesCtrl: this.task.notes ? this.task.notes : "",
      summaryCtrl: this.task.summary ? this.task.summary : "",
    });

    this.visibleToForUserControl.setValue(this.task.isVisibleToSubject);
  }

  public ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  public autocompleteDisplayUserOption(
    user?: UserInterface
  ): string | undefined {
    // Static text to Language Variable updated
    if (!user || !(user.firstName || user.lastName)) {
      return this._translateService.instant("$message_deleted_user");
    }
    return user ? `${user.firstName} ${user.lastName}` : undefined;
  }

  public performTask(): void {
    switch (this.relatedEntity.entityType) {
      case EntityType.SURVEY:
        this.goToSurvey();
        break;
      case EntityType.EVALUATION:
        this.goToEvaluation();
        break;
      default:
        this.closeTask();
        break;
    }

    this._dialogRef.close();
  }

  public getTypeString(type: string): string {
    switch (type.toLowerCase()) {
      case EntityType.SURVEY:
        return EntityTypeDynamic.SURVEY;
      case EntityType.EVALUATION:
        return EntityTypeDynamic.EVALUATION;
      default:
        return type;
    }
  }
  public openPdfDialog(): void {
    // checking for tablet device
    if (utilityCommon.isTablet()) {
      this.showSnackBar(this._translateService.instant("$pdf_ios_preparing"));
      window.location.assign(
        `/api/pdf/assessment/${this.relatedEntity.entityId}`
      );
    } else {
      // Download pdf in Safari
      if (this.browser === "Safari" && this.isLinked) {
        this.showSnackBar(
          this._translateService.instant("$pdf_android_download")
        );
        utilityCommon.pdfDownloadinSafari(this.relatedEntity.entityId, "");
      } else {
        if (
          this.isLinked &&
          this.relatedEntity.entityType.toLowerCase() ===
            EntityType.EVALUATION.toLowerCase()
        ) {
          this.dialog.open(ErgonomicsEvaluationReportDialogComponent, {
            height: "100%",
            width: "1000px",
            data: {
              evaluationId: this.relatedEntity.entityId,
            },
          });
          this._dialogRef.close();
        }
        if (
          this.isLinked &&
          this.relatedEntity.entityType.toLowerCase() ===
            EntityType.SURVEY.toLowerCase()
        ) {
          this.dialog.open(SurveyReportDialogComponent, {
            height: "100%",
            width: "1000px",
            data: {
              surveyId: this.relatedEntity.entityId,
            },
          });
          this._dialogRef.close();
        }
      }
    }
  }

  public getAssignToUser() {
    if (
      this.loggedInUser.id == this.task.assignedTo.id ||
      this.loggedInUser.roleLabel == "Administrator (master)"
    ) {
      return false;
    }
    return true;
  }

  public showCommentsContainer(): void {
    this.showComments = true;
    this.indicateComments = false;
  }

  public updateTask(): void {
    let taskUpdates: TaskInterface = {
      ...this.task,
      assignedTo: this.taskFormGroup.value.assignedToCtrl,
      hours: this.taskFormGroup.value.hoursCtrl,
      notes: this.taskFormGroup.value.notesCtrl,
      summary: this.taskFormGroup.value.summaryCtrl,
      isVisibleToSubject: this.visibleToForUserControl.value,
    };

    // we only show one of these FormControls in the UI depending .isComplete
    if (this.task.isComplete) {
      taskUpdates = {
        ...taskUpdates,
        completedDate: new Date(this.taskFormGroup.value.completedDateCtrl),
      };
    } else {
      taskUpdates = {
        ...taskUpdates,
        dueDate: new Date(this.taskFormGroup.value.dueDateCtrl),
      };
    }

    this._taskService.update(taskUpdates);

    if (location.pathname === "/my-zone/evaluation") {
      // Static text to Language Variable updated
      this._snackBar.open(
        this._translateService.instant("$message_task_reassigned"),
        undefined,
        {
          duration: 2000,
        }
      );
      this._browserService.toHome();
    }
  }

  public closeTask(): void {
    this._taskService.close(this.task);
  }

  public getTaskButtonCopy(): string {
    return this._translateService.instant(
      this.mapStatus(this.task.status, this.isLinked)
    );
  }

  private showSnackBar(message: string): void {
    this._snackBar.open(message, undefined, {
      duration: 5000,
    });
  }

  private filterAssignToUserOptions(name: string): UserInterface[] {
    const filterValue: string = name.toLowerCase();

    return this.allAssignedToOptions.filter(
      (option: UserInterface) =>
        option.firstName.toLowerCase().indexOf(filterValue) === 0
    );
  }

  private goToEvaluation(): void {
    // update current evaluation related task in the evaluation store
    this._evaluationService
      .updateCurrentEvaluationRelatedTask(this.task)
      .pipe(takeUntil(this._destroy$))
      .subscribe((task: TaskInterface) => {
        if (!task) {
          return;
        }

        // go to the evaluation page
        this._browserService.toEvaluation();
      });
  }

  private goToSurvey(): void {
    this._browserService.toSurvey({ taskid: this.task.id });
  }
}
